import { useCallback, useMemo, useState } from "react";
import { useAppKit } from "@reown/appkit/react";
import { PublicClient, WalletClient } from "viem";
import {
  useConnect,
  useConnection,
  useConnectors,
  usePublicClient,
  useSignMessage,
  useSwitchChain,
  useWalletClient
} from "wagmi";
import { EXPECTED_CHAIN } from "../utils/config";
import { isReownConfigured } from "../wallet/appkit";

export interface WalletOption {
  id: string;
  name: string;
  rdns?: string;
  icon?: string;
}

type ConnectOptions = {
  forcePermission?: boolean;
};

const APPKIT_WALLET_ID = "appkit-all-wallets";
const SELECTED_WALLET_STORAGE_KEY = "codeape.selectedWalletProvider";
const NO_WALLET_MESSAGE = "No wallet connector is available. On mobile, open this page inside a wallet browser or use the all-wallets modal.";

function readSelectedWalletId(): string | null {
  try {
    return window.localStorage.getItem(SELECTED_WALLET_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeSelectedWalletId(id: string): void {
  try {
    window.localStorage.setItem(SELECTED_WALLET_STORAGE_KEY, id);
  } catch {
    // Non-critical; wallet selection still works for this render.
  }
}

function normalizeWalletError(error: unknown, fallback: string): string {
  if (typeof error !== "object" || error === null) return fallback;
  const code = (error as { code?: number | string }).code;
  if (code === -32002) return "A wallet request is already open. Open your wallet and confirm or reject the pending request, then try again.";
  if (code === 4001) return "Wallet request rejected.";
  if (error instanceof Error && error.message) return error.message;
  const shortMessage = (error as { shortMessage?: string }).shortMessage;
  return shortMessage || fallback;
}

function connectorName(name: string): string {
  if (name.toLowerCase().includes("walletconnect")) return "WalletConnect";
  if (name.toLowerCase().includes("injected")) return "Browser Wallet";
  return name;
}

function firstConnectedAccount(result: { accounts?: readonly unknown[] }): `0x${string}` | null {
  const first = result.accounts?.[0];
  return typeof first === "string" && first.startsWith("0x") ? first as `0x${string}` : null;
}

/** Wallet hook backed by wagmi and Reown AppKit, with the legacy page API preserved. */
export function useWallet() {
  const connection = useConnection();
  const connectors = useConnectors();
  const { open } = useAppKit();
  const { connectAsync, isPending: connectPending } = useConnect();
  const { switchChainAsync, isPending: switchPending } = useSwitchChain();
  const { signMessageAsync } = useSignMessage();
  const { data: walletClientData } = useWalletClient();
  const publicClient = usePublicClient({ chainId: EXPECTED_CHAIN.id });
  const [selectedWalletId, setSelectedWalletId] = useState<string | null>(() => readSelectedWalletId() ?? (isReownConfigured ? APPKIT_WALLET_ID : null));
  const [localConnecting, setLocalConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const walletOptions = useMemo<WalletOption[]>(() => {
    const options: WalletOption[] = [];
    if (isReownConfigured) {
      options.push({ id: APPKIT_WALLET_ID, name: "All wallets" });
    }
    for (const connector of connectors) {
      options.push({ id: connector.uid, name: connectorName(connector.name), rdns: connector.id });
    }
    const unique = new Map<string, WalletOption>();
    for (const option of options) {
      if (!unique.has(option.id)) unique.set(option.id, option);
    }
    return [...unique.values()];
  }, [connectors]);

  const activeSelectedWalletId = selectedWalletId ?? walletOptions[0]?.id ?? null;

  const selectWallet = useCallback((id: string) => {
    writeSelectedWalletId(id);
    setSelectedWalletId(id);
    setError(null);
  }, []);

  const connect = useCallback(async (_options: ConnectOptions = {}): Promise<`0x${string}` | null> => {
    setLocalConnecting(true);
    setError(null);
    try {
      const selectedId = activeSelectedWalletId ?? (isReownConfigured ? APPKIT_WALLET_ID : connectors[0]?.uid ?? null);
      if (!selectedId) throw new Error(NO_WALLET_MESSAGE);
      writeSelectedWalletId(selectedId);
      setSelectedWalletId(selectedId);

      if (selectedId === APPKIT_WALLET_ID && isReownConfigured) {
        await open({ view: "Connect" });
        return null;
      }

      const connector = connectors.find((candidate) => candidate.uid === selectedId) ?? connectors[0];
      if (!connector) throw new Error(NO_WALLET_MESSAGE);
      const result = await connectAsync({ connector, chainId: EXPECTED_CHAIN.id });
      const connected = firstConnectedAccount(result);
      if (!connected) throw new Error("Wallet returned no account. Unlock your wallet and try again.");
      return connected;
    } catch (e: unknown) {
      setError(normalizeWalletError(e, "Connection failed."));
      return null;
    } finally {
      setLocalConnecting(false);
    }
  }, [activeSelectedWalletId, connectAsync, connectors, open]);

  const switchToExpectedChain = useCallback(async (): Promise<boolean> => {
    if (connection.chainId === EXPECTED_CHAIN.id) {
      setError(null);
      return true;
    }
    try {
      await switchChainAsync({ chainId: EXPECTED_CHAIN.id });
      setError(null);
      return true;
    } catch (e: unknown) {
      setError(normalizeWalletError(e, "Failed to switch network."));
      return false;
    }
  }, [connection.chainId, switchChainAsync]);

  const personalSign = useCallback(async (message: string, accountOverride?: `0x${string}` | null): Promise<`0x${string}`> => {
    const account = accountOverride ?? connection.address ?? null;
    if (!account) throw new Error(NO_WALLET_MESSAGE);
    try {
      const signature = await signMessageAsync({ message, account });
      if (typeof signature !== "string" || !/^0x[0-9a-fA-F]+$/.test(signature)) {
        throw new Error("Wallet returned an invalid signature.");
      }
      return signature as `0x${string}`;
    } catch (e: unknown) {
      throw new Error(normalizeWalletError(e, "Signature failed."));
    }
  }, [connection.address, signMessageAsync]);

  const getWalletClient = useCallback((accountOverride?: `0x${string}` | null): WalletClient | null => {
    const account = accountOverride ?? connection.address ?? null;
    if (!account || !walletClientData) return null;
    return walletClientData as WalletClient;
  }, [connection.address, walletClientData]);

  return {
    account: (connection.address ?? null) as `0x${string}` | null,
    chainId: connection.chainId ?? null,
    isConnecting: localConnecting || connectPending || switchPending || connection.isConnecting || connection.isReconnecting,
    error,
    walletOptions,
    selectedWalletId: activeSelectedWalletId,
    connect,
    switchToExpectedChain,
    personalSign,
    getWalletClient,
    walletClient: getWalletClient(),
    publicClient: (publicClient ?? null) as PublicClient | null,
    selectWallet
  };
}
